/**
 * Catálogo de block types soportados por el Designer.
 *
 * Filosofía: primitivas mínimas + composición. ADR / AC / external_link
 * no son tipos atómicos — son combinaciones de primitivos. Lo frecuente
 * se cubre con presets más adelante, no con types hardcodeados.
 *
 * Cada entrada lleva:
 *   - `value`: el string que se persiste en `sections[].type`.
 *   - `family`: agrupación en el picker modal (Contenido fijo, Texto, etc.).
 *   - `name`: título corto para mostrar en el picker (localizable, pero
 *     hoy es el nombre canónico en español; el picker usa `t()` cuando
 *     el i18n key existe en `templates.types.<value>.name`).
 *   - `description`: descripción corta para el picker / tooltip.
 *   - `available`: si el Designer permite seleccionarlo hoy.
 *   - `pendingTag`: cuando `available=false`, etiqueta corta (`v2`,
 *     `Commit E`, `B.7`) para mostrar en la card.
 *   - `defaultExtras`: campos extra que se inyectan al crear una sección
 *     nueva (ej. `children: []` para fixed_heading).
 *
 * El validador backend acepta TODOS los tipos listados en
 * `app/schemas/template_definition.py::BlockType`; el Designer expone
 * solo los `available: true`.
 */

import type { BlockType } from '../../../api/templates'

/** Familias visuales del picker modal. Cada familia se renderiza como
 * un grupo con título + subtítulo, con sus block types dentro como
 * cards. Orden de declaración = orden de aparición. */
export type BlockFamily =
  | 'fixed'
  | 'text'
  | 'list'
  | 'table'
  | 'diagram'
  | 'code'
  | 'files'

export interface BlockFamilyMeta {
  key: BlockFamily
  /** Localizable. */
  titleKey: string
  /** Localizable. */
  subtitleKey: string
}

/** Familias en orden de aparición en el picker. */
export const BLOCK_FAMILIES: BlockFamilyMeta[] = [
  {
    key: 'fixed',
    titleKey: 'designer.picker.family_fixed_title',
    subtitleKey: 'designer.picker.family_fixed_subtitle',
  },
  {
    key: 'text',
    titleKey: 'designer.picker.family_text_title',
    subtitleKey: 'designer.picker.family_text_subtitle',
  },
  {
    key: 'list',
    titleKey: 'designer.picker.family_list_title',
    subtitleKey: 'designer.picker.family_list_subtitle',
  },
  {
    key: 'table',
    titleKey: 'designer.picker.family_table_title',
    subtitleKey: 'designer.picker.family_table_subtitle',
  },
  {
    key: 'diagram',
    titleKey: 'designer.picker.family_diagram_title',
    subtitleKey: 'designer.picker.family_diagram_subtitle',
  },
  {
    key: 'code',
    titleKey: 'designer.picker.family_code_title',
    subtitleKey: 'designer.picker.family_code_subtitle',
  },
  {
    key: 'files',
    titleKey: 'designer.picker.family_files_title',
    subtitleKey: 'designer.picker.family_files_subtitle',
  },
]

export interface BlockTypeMeta {
  value: BlockType
  family: BlockFamily
  /** Texto corto para mostrar en pickers/badges (no localizado — usar
   * `t('templates.designer.block_types.<value>')` cuando se quiera i18n). */
  shortLabel: string
  /** Nombre largo para la card del picker. */
  name: string
  /** Frase corta describiendo el bloque. Para tooltips/help. */
  description: string
  available: boolean
  /** Cuando available=false, etiqueta corta a mostrar en la card. */
  pendingTag?: string
  /** Campos estructurales por default cuando se crea una sección nueva. */
  defaultExtras?: Record<string, unknown>
}

export const BLOCK_TYPES: BlockTypeMeta[] = [
  // ─── Contenido fijo ───────────────────────────────────────────────
  {
    value: 'fixed_heading',
    family: 'fixed',
    shortLabel: 'grupo',
    name: 'Grupo anidable',
    description:
      'Agrupa otros nodos. Único tipo que acepta hijos. Marca como repetible para que el usuario instancie N copias del subárbol en runtime (ej. N casos de uso).',
    available: true,
    defaultExtras: { children: [] },
  },
  {
    value: 'fixed_block',
    family: 'fixed',
    shortLabel: 'callout',
    name: 'Callout fijo',
    description:
      'Texto fijo declarado en la plantilla (intro, advertencia, nota legal). No se llena en runtime — el usuario solo lo lee.',
    available: true,
  },

  // ─── Texto ────────────────────────────────────────────────────────
  {
    value: 'rich_text',
    family: 'text',
    shortLabel: 'rich',
    name: 'Texto rich',
    description:
      'Texto formateado con párrafos. Editor WYSIWYG con bold, headings, listas, citas (TipTap llega en B.6.c.3).',
    available: true,
  },
  {
    value: 'plain_text',
    family: 'text',
    shortLabel: 'plain',
    name: 'Texto plano',
    description:
      'Texto corto, una sola línea sin formato.',
    available: true,
  },
  {
    value: 'key_value_grid',
    family: 'text',
    shortLabel: 'kv',
    name: 'Pares clave/valor',
    description:
      'Metadata en grid: ID, versión, status, sponsor, etc. Keys libres o predefinidos.',
    available: true,
  },

  // ─── Listas ───────────────────────────────────────────────────────
  {
    value: 'numbered_list',
    family: 'list',
    shortLabel: '1. lista',
    name: 'Lista numerada',
    description:
      'Items secuenciales o numerables: pasos, restricciones, supuestos.',
    available: true,
  },
  {
    value: 'bullet_list',
    family: 'list',
    shortLabel: 'lista',
    name: 'Lista con viñetas',
    description: 'Items sin orden estricto: tecnologías, stakeholders, scope.',
    available: true,
  },
  {
    value: 'checklist',
    family: 'list',
    shortLabel: '☑ lista',
    name: 'Checklist',
    description:
      'Items con estado completado / no — Definition of Done, pre-requisitos, criterios verificables. Hereda config de bullet_list + checkbox por item.',
    available: true,
  },

  // ─── Tabla estructurada ───────────────────────────────────────────
  {
    value: 'structured_table',
    family: 'table',
    shortLabel: 'tabla',
    name: 'Tabla estructurada',
    description:
      'Tabla con schema de columnas tipadas (text, number, date, enum, select, autonum) que la IA respeta. El template author declara las columnas; el usuario solo añade filas.',
    available: true,
    defaultExtras: {
      columns: [],
      min_rows: 1,
    },
  },

  // ─── Diagramas Mermaid ────────────────────────────────────────────
  {
    value: 'mermaid_flowchart',
    family: 'diagram',
    shortLabel: 'flowchart',
    name: 'Flowchart',
    description: 'Procesos con decisiones, loops, ramificaciones.',
    available: true,
  },
  {
    value: 'mermaid_sequence',
    family: 'diagram',
    shortLabel: 'sequence',
    name: 'Diagrama de secuencia',
    description: 'Mensajes entre actores en el tiempo.',
    available: true,
  },
  {
    value: 'mermaid_use_case',
    family: 'diagram',
    shortLabel: 'use case',
    name: 'Caso de uso UML',
    description: 'Actores + use cases + sistema.',
    available: true,
  },
  {
    value: 'mermaid_activity',
    family: 'diagram',
    shortLabel: 'activity',
    name: 'Actividades UML',
    description: 'Flujo paso a paso de un caso.',
    available: true,
  },
  {
    value: 'mermaid_state',
    family: 'diagram',
    shortLabel: 'state',
    name: 'Máquina de estados',
    description: 'Estados y transiciones.',
    available: true,
  },
  {
    value: 'mermaid_er',
    family: 'diagram',
    shortLabel: 'ER',
    name: 'Modelo ER',
    description: 'Entidades y relaciones de datos.',
    available: true,
  },
  {
    value: 'mermaid_class',
    family: 'diagram',
    shortLabel: 'class',
    name: 'Clases UML',
    description: 'Clases, atributos, herencia.',
    available: true,
  },
  {
    value: 'mermaid_gantt',
    family: 'diagram',
    shortLabel: 'gantt',
    name: 'Gantt',
    description: 'Cronograma de tareas en el tiempo.',
    available: true,
  },
  {
    value: 'mermaid_journey',
    family: 'diagram',
    shortLabel: 'journey',
    name: 'User journey',
    description: 'Recorrido del usuario por etapas con scoring.',
    available: true,
  },
  {
    value: 'mermaid_mindmap',
    family: 'diagram',
    shortLabel: 'mindmap',
    name: 'Mindmap',
    description: 'Mapa mental de ideas.',
    available: true,
  },
  {
    value: 'mermaid_generic',
    family: 'diagram',
    shortLabel: 'mermaid',
    name: 'Mermaid genérico',
    description:
      'Cualquier sintaxis Mermaid · escape hatch cuando no encaja en los sub-types.',
    available: true,
  },
  {
    value: 'diagram',
    family: 'diagram',
    shortLabel: 'diagrama v1',
    name: 'Diagrama Mermaid (legacy)',
    description:
      'Diagrama Mermaid sin sub-tipo declarado. Deprecado — usa los mermaid_* sub-types cuando estén disponibles (Commit E). Las plantillas que ya tengan `diagram` siguen funcionando, pero no se pueden crear nuevas.',
    available: false,
    pendingTag: 'deprecada',
  },

  // ─── Código y datos ───────────────────────────────────────────────
  {
    value: 'code_block',
    family: 'code',
    shortLabel: 'code',
    name: 'Bloque de código',
    description:
      'Snippet con lenguaje declarado: SQL, JSON, TypeScript, YAML, etc.',
    available: true,
  },
  {
    value: 'structured',
    family: 'code',
    shortLabel: 'json',
    name: 'JSON estructurado',
    description:
      'Objeto JSON crudo con schema propio. Para casos avanzados / debug — el usuario común usa structured_table.',
    available: true,
  },

  // ─── Archivos y referencias ───────────────────────────────────────
  {
    value: 'image_upload',
    family: 'files',
    shortLabel: 'imagen',
    name: 'Imagen',
    description:
      'Mockup, captura, foto. Requiere Azure Blob Storage (Fase B.7).',
    available: false,
    pendingTag: 'B.7',
  },
  {
    value: 'file_upload',
    family: 'files',
    shortLabel: 'archivo',
    name: 'Archivo adjunto',
    description:
      'PDF, Excel, contrato firmado. Requiere Azure Blob Storage (Fase B.7).',
    available: false,
    pendingTag: 'B.7',
  },
  {
    // 'embed_canon' is the literal API block type value (kept verbatim).
    value: 'embed_canon',
    family: 'files',
    shortLabel: 'cita',
    name: 'Embed official',
    description:
      'Cita un fragmento de otro documento oficial (v2 — post-MVP, requiere wiring de referencias cruzadas).',
    available: false,
    pendingTag: 'v2',
  },
]

const BY_VALUE = new Map<BlockType, BlockTypeMeta>(
  BLOCK_TYPES.map((b) => [b.value, b]),
)

export function getBlockMeta(value: BlockType): BlockTypeMeta | undefined {
  return BY_VALUE.get(value)
}

/** True si el Designer puede seleccionar este block type hoy. */
export function isAvailableBlockType(value: BlockType): boolean {
  return BY_VALUE.get(value)?.available ?? false
}

/** Lista plana de los block types disponibles para el selector del
 * editor — útil para evitar que el user cree algo no soportado. */
export const AVAILABLE_BLOCK_TYPES: BlockType[] = BLOCK_TYPES.filter(
  (b) => b.available,
).map((b) => b.value)

/** Agrupa los block types por familia. Devuelve solo familias que
 * tienen al menos un type. Mantiene el orden declarado en BLOCK_FAMILIES. */
export function groupBlockTypesByFamily(): Array<{
  family: BlockFamilyMeta
  types: BlockTypeMeta[]
}> {
  const grouped = BLOCK_FAMILIES.map((family) => ({
    family,
    types: BLOCK_TYPES.filter((b) => b.family === family.key),
  })).filter((g) => g.types.length > 0)
  return grouped
}
